import axios, { AxiosInstance, AxiosResponse, AxiosError } from 'axios';
import { env, getApiBaseUrl } from '@/config/environment';
import { isTokenExpired } from '@/utils/jwtUtils';

/**
 * API Error type
 */
export class ApiError extends Error {
  constructor(
    message: string,
    public status?: number,
    public data?: unknown,
    public detail?: string
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

/**
 * Typed API service client (extends AxiosInstance)
 * Avoids use of `any` for augmentation methods
 */
export interface ApiServiceClient extends AxiosInstance {
  setToken: (newToken: string) => void;
  clearToken: () => void;
  getToken: () => string | null;
  setUnauthorizedCallback: (callback: () => void) => void;
}

/**
 * API Configuration
 */
const API_CONFIG = {
  baseURL: getApiBaseUrl(),
  timeout: env.API_TIMEOUT,
  headers: {
    'Content-Type': 'application/json',
    'ngrok-skip-browser-warning': 'true',
  },
};

/**
 * Create API service that returns axios instance with interceptors
 */
export const createApiService = (onUnauthorized?: () => void): ApiServiceClient => {
  let token: string | null = null;
  let unauthorizedCallback = onUnauthorized;

  // Create axios instance
  const client = axios.create(API_CONFIG) as ApiServiceClient;

  /**
   * Initialize token from localStorage
   */
  const initializeToken = (): void => {
    if (typeof window !== 'undefined') {
      const storedToken = localStorage.getItem(env.JWT_STORAGE_KEY);
      if (storedToken && !isTokenExpired(storedToken)) {
        token = storedToken;
      } else if (storedToken) {
        // Clear expired token and related user data
        localStorage.removeItem(env.JWT_STORAGE_KEY);
        localStorage.removeItem(env.USER_STORAGE_KEY);
        token = null;
      }
    }
  };

  /**
   * Handle unauthorized access
   */
  const handleUnauthorized = (): void => {
    token = null;
    
    // Clear localStorage
    if (typeof window !== 'undefined') {
      localStorage.removeItem(env.JWT_STORAGE_KEY);
      localStorage.removeItem(env.USER_STORAGE_KEY);
    }
    
    // Call unauthorized callback
    if (unauthorizedCallback) {
      unauthorizedCallback();
    }
  };

  /**
   * Handle API errors
   */
  const handleError = (error: AxiosError): ApiError => {
    const extractMessage = (data: unknown): string | undefined => {
      if (typeof data === 'object' && data !== null) {
        const maybeMessage = (data as { message?: unknown }).message;
        if (typeof maybeMessage === 'string') return maybeMessage;
      }
      return undefined;
    };
    if (error.response) {
      // Server responded with error status
      const message =
        extractMessage(error.response.data) ||
        error.message ||
        `HTTP ${error.response.status}: ${error.response.statusText}`;
      
      return new ApiError(
        message,
        error.response.status,
        error.response.data
      );
    } else if (error.request) {
      // Request made but no response
      return new ApiError('No response from server', undefined, error.request as unknown);
    } else {
      // Error setting up request
      return new ApiError(error.message || 'An unknown error occurred');
    }
  };

  /**
   * Setup request and response interceptors
   */
  const setupInterceptors = (): void => {
    // Request interceptor
    client.interceptors.request.use(
      (config) => {
        // Add authorization token if available
        if (token) {
          config.headers.Authorization = `Bearer ${token}`;
        }

        // Add timestamp to requests for debugging
        if (env.IS_DEVELOPMENT) {
          console.log(`[API Request] ${config.method?.toUpperCase()} ${config.url}`);
        }

        return config;
      },
      (error) => {
        return Promise.reject(error);
      }
    );

    // Response interceptor
    client.interceptors.response.use(
      (response: AxiosResponse) => {
        if (env.IS_DEVELOPMENT) {
          console.log(`[API Response] ${response.config.method?.toUpperCase()} ${response.config.url}`, response.status);
        }
        return response;
      },
      (error: AxiosError) => {
        const apiError = handleError(error);
        
        // Handle 401 Unauthorized
        if (error.response?.status === 401) {
          handleUnauthorized();
        }
        
        return Promise.reject(apiError);
      }
    );
  };

  // Add token management methods to the client
  client.setToken = (newToken: string): void => {
    token = newToken;
    if (typeof window !== 'undefined') {
      localStorage.setItem(env.JWT_STORAGE_KEY, newToken);
    }
  };

  client.clearToken = (): void => {
    token = null;
    if (typeof window !== 'undefined') {
      localStorage.removeItem(env.JWT_STORAGE_KEY);
      localStorage.removeItem(env.USER_STORAGE_KEY);
    }
  };

  client.getToken = (): string | null => {
    return token;
  };

  client.setUnauthorizedCallback = (callback: () => void): void => {
    unauthorizedCallback = callback;
  };

  // Initialize the service
  initializeToken();
  setupInterceptors();

  // Return the axios instance
  return client;
};

// Create singleton instance
let sharedApiService: ApiServiceClient | null = null;

/**
 * Get shared API service instance
 */
export const getSharedApiService = (): ApiServiceClient => {
  if (!sharedApiService) {
    sharedApiService = createApiService();
  }
  return sharedApiService;
};

/**
 * Set unauthorized callback for shared instance
 */
export const setUnauthorizedCallback = (callback: () => void): void => {
  const service = getSharedApiService();
  service.setUnauthorizedCallback(callback);
};

